// mesbot/src/institution/pages/Students.js
import React, { useEffect, useState, useCallback } from "react";
import { FiLock } from "react-icons/fi";
import { getStudents, getStudentProfile, patchStudent } from "../api";

function StatusBadge({ isActive }) {
  const cls = isActive ? "active" : "deactivated";
  return <span className={`inst-badge ${cls}`}>{isActive ? "Active" : "Deactivated"}</span>;
}

function ProfileModal({ token, student, onClose, onToggleActive }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStudentProfile(token, student.id)
      .then(setProfile)
      .finally(() => setLoading(false));
  }, [token, student.id]);

  return (
    <div className="inst-modal-overlay" onClick={onClose}>
      <div className="inst-modal" onClick={e => e.stopPropagation()}>
        <div className="inst-modal-title">Student Profile</div>
        {loading ? (
          <p style={{ color: "#6b7280" }}>Loading...</p>
        ) : profile ? (
          <>
            <div className="inst-form-group">
              <label className="inst-label">Name</label>
              <input className="inst-input readonly" readOnly value={profile.name || profile.username} />
            </div>
            <div className="inst-form-group">
              <label className="inst-label">Email</label>
              <input className="inst-input readonly" readOnly value={profile.email || "—"} />
            </div>
            <div className="inst-form-group">
              <label className="inst-label">Cohort</label>
              <input className="inst-input readonly" readOnly value={profile.cohort_name || "Open enrollment"} />
            </div>
            <div className="inst-form-group">
              <label className="inst-label">Last Active</label>
              <input className="inst-input readonly" readOnly value={profile.last_active ? new Date(profile.last_active).toLocaleString() : "Never"} />
            </div>
            <div className="inst-form-group">
              <label className="inst-label">Status</label>
              <div><StatusBadge isActive={profile.is_active} /></div>
            </div>
            <p style={{ fontSize: 10, color: "#9ca3af", marginBottom: 16 }}>
              This profile view has been logged per FERPA audit requirements.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                className={`inst-btn ${profile.is_active ? "inst-btn-danger" : "inst-btn-primary"}`}
                onClick={() => onToggleActive(profile.id, !profile.is_active)}
              >
                {profile.is_active ? "Deactivate" : "Reactivate"}
              </button>
              <button className="inst-btn inst-btn-ghost" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <p style={{ color: "#ef4444" }}>Failed to load profile.</p>
        )}
      </div>
    </div>
  );
}

export default function Students({ token }) {
  const [students, setStudents] = useState([]);
  const [duaRequired, setDuaRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);

  const fetchStudents = useCallback(() => {
    setLoading(true);
    getStudents(token)
      .then(data => { setStudents(data); setDuaRequired(false); })
      .catch(err => {
        if (err.status === 403) setDuaRequired(true);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const filtered = students.filter(s => {
    const matchName = !search || (s.name && s.name.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = !statusFilter || (statusFilter === "active" ? s.is_active : !s.is_active);
    return matchName && matchStatus;
  });

  async function handleToggleActive(studentId, newActive) {
    await patchStudent(token, studentId, newActive);
    setSelectedStudent(null);
    fetchStudents();
  }

  if (duaRequired) {
    return (
      <div className="inst-dua-lock">
        <FiLock size={40} color="#9ca3af" />
        <h3>Data Use Agreement Required</h3>
        <p style={{ maxWidth: 360, lineHeight: 1.6 }}>
          Roster access requires a signed Data Use Agreement on file with Rigby.
          Contact <strong>support@myemotionalsupportbot.com</strong> to get started.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="inst-page-title">Students</div>
      <div className="inst-page-subtitle">
        {students.length} enrolled · click a row to view profile
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          className="inst-input"
          placeholder="Search by name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
        <select
          className="inst-input"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ width: 160 }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="deactivated">Deactivated</option>
        </select>
      </div>

      <div className="inst-panel inst-table-wrap">
        <table className="inst-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Cohort</th>
              <th>Last Active</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ color: "#6b7280", textAlign: "center", padding: 20 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={4} style={{ color: "#6b7280", textAlign: "center", padding: 20 }}>No students found</td></tr>
            ) : filtered.map(s => (
              <tr key={s.id} onClick={() => setSelectedStudent(s)}>
                <td>{s.name}</td>
                <td style={{ color: "#6b7280" }}>{s.cohort_name || "Open"}</td>
                <td style={{ color: "#6b7280" }}>
                  {s.last_active ? new Date(s.last_active).toLocaleDateString() : "Never"}
                </td>
                <td><StatusBadge isActive={s.is_active} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 10, color: "#9ca3af", padding: "8px 14px" }}>
          No message content is ever visible to administrators.
        </div>
      </div>

      {selectedStudent && (
        <ProfileModal
          token={token}
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onToggleActive={handleToggleActive}
        />
      )}
    </>
  );
}
